import { ArticleBusinessType } from 'admin/articleService'
import { CategoryFromApi } from 'products/types'
import { getProductName } from 'products/productTable/utils'
import { getBreadcrumbs } from 'products/redux'
import { PRODUCTS } from 'capacity/copy'
import { t } from 'translations'

export type CategoryNames = Parameters<typeof getBreadcrumbs>[0]

export const categoryNamesFrom = (categories: CategoryFromApi[]): CategoryNames =>
    Object.fromEntries(
        categories.map((category) => [
            category.id,
            { name: getProductName(category.name), parentId: category.parentCategory?.id },
        ]),
    )

export function productBreadcrumb(categoryIds: string[], categoryNames: CategoryNames): string {
    const paths = categoryIds.map((id) => getBreadcrumbs(categoryNames, id)).filter((path) => path.length > 0)
    if (paths.length === 0) {
        return PRODUCTS.breadcrumb.none
    }
    if (paths.length === 1) {
        return paths[0].join(PRODUCTS.breadcrumb.separator)
    }
    return PRODUCTS.breadcrumb.many(paths.length)
}

// Getters translate when read, so the labels follow the active language.
export const PRODUCT_TYPE_LABEL: Record<ArticleBusinessType, { one: string; many: string }> = {
    single_visit_ticket: {
        get one() {
            return t('capacity.productTypes.single_visit_ticket.one')
        },
        get many() {
            return t('capacity.productTypes.single_visit_ticket.many')
        },
    },
    multi_visit_ticket: {
        get one() {
            return t('capacity.productTypes.multi_visit_ticket.one')
        },
        get many() {
            return t('capacity.productTypes.multi_visit_ticket.many')
        },
    },
    visit_reservation: {
        get one() {
            return t('capacity.productTypes.visit_reservation.one')
        },
        get many() {
            return t('capacity.productTypes.visit_reservation.many')
        },
    },
    upsell: {
        get one() {
            return t('capacity.productTypes.upsell.one')
        },
        get many() {
            return t('capacity.productTypes.upsell.many')
        },
    },
    service: {
        get one() {
            return t('capacity.productTypes.service.one')
        },
        get many() {
            return t('capacity.productTypes.service.many')
        },
    },
    service_date_change: {
        get one() {
            return t('capacity.productTypes.service_date_change.one')
        },
        get many() {
            return t('capacity.productTypes.service_date_change.many')
        },
    },
    service_fee: {
        get one() {
            return t('capacity.productTypes.service_fee.one')
        },
        get many() {
            return t('capacity.productTypes.service_fee.many')
        },
    },
    bundle: {
        get one() {
            return t('capacity.productTypes.bundle.one')
        },
        get many() {
            return t('capacity.productTypes.bundle.many')
        },
    },
    season_pass: {
        get one() {
            return t('capacity.productTypes.season_pass.one')
        },
        get many() {
            return t('capacity.productTypes.season_pass.many')
        },
    },
    membership: {
        get one() {
            return t('capacity.productTypes.membership.one')
        },
        get many() {
            return t('capacity.productTypes.membership.many')
        },
    },
    voucher: {
        get one() {
            return t('capacity.productTypes.voucher.one')
        },
        get many() {
            return t('capacity.productTypes.voucher.many')
        },
    },
    donation: {
        get one() {
            return t('capacity.productTypes.donation.one')
        },
        get many() {
            return t('capacity.productTypes.donation.many')
        },
    },
    loyalty_card: {
        get one() {
            return t('capacity.productTypes.loyalty_card.one')
        },
        get many() {
            return t('capacity.productTypes.loyalty_card.many')
        },
    },
    food_and_beverage: {
        get one() {
            return t('capacity.productTypes.food_and_beverage.one')
        },
        get many() {
            return t('capacity.productTypes.food_and_beverage.many')
        },
    },
}

export const OPTION_TYPE_LABEL = {
    get one() {
        return t('capacity.productTypes.option.one')
    },
    get many() {
        return t('capacity.productTypes.option.many')
    },
}

const FALLBACK_TYPE_LABEL = {
    get one() {
        return t('capacity.productTypes.fallback.one')
    },
    get many() {
        return t('capacity.productTypes.fallback.many')
    },
}

export const isKnownBusinessType = (value: string | null | undefined): value is ArticleBusinessType =>
    !!value && Object.prototype.hasOwnProperty.call(PRODUCT_TYPE_LABEL, value)

export const businessTypeLabel = (businessType: string | null | undefined): { one: string; many: string } =>
    isKnownBusinessType(businessType) ? PRODUCT_TYPE_LABEL[businessType] : FALLBACK_TYPE_LABEL

export const productTypeLabel = (businessType: ArticleBusinessType | null, optionGroupName?: string): string => {
    if (optionGroupName) {
        return t('capacity.productTypes.optionIn', { group: optionGroupName })
    }
    return businessTypeLabel(businessType).one
}

export const PASS_TYPES: ArticleBusinessType[] = ['membership', 'season_pass']

export const isPassType = (businessType: ArticleBusinessType | null | undefined): boolean =>
    !!businessType && PASS_TYPES.includes(businessType)
